import { closeChecklistURLs } from 'routes/close/closeURLs';

import { UserAvatar } from 'components/data-display/UserAvatar';

import { useUsersData } from 'hooks/useUsersData';

import { featureFlags } from 'utils/config/featureFlags';
import { getName } from 'utils/users/config';

import { useAuthStore } from 'store/auth.store';

import { GenericNotificationItem } from 'layout/inbox-notifications/GenericNotificationItem';

import {
  TaskTemplateCreatedNotification,
  TaskTemplateDeletedNotification,
} from 'types/inbox-notifications/close/ChecklistNotifications';
import { SegmentAtByDot } from 'types/utils';

const { FF__SHOW_NOTIFICATIONS_DEBUG_MODE: DEBUG_MODE } = featureFlags;

export type TaskTemplateNotification =
  | TaskTemplateCreatedNotification
  | TaskTemplateDeletedNotification;

type TaskTemplateLevelKeys = SegmentAtByDot<
  TaskTemplateNotification['type'],
  4
>;

export function TemplateNotificationItem({
  type,
  item,
}: {
  type: string[];
  item: TaskTemplateNotification;
}) {
  const [actionType, ..._restOfType] = type;
  if (!item.template) {
    return <div>Notification task template not found</div>;
  }

  switch (actionType as TaskTemplateLevelKeys) {
    case 'created':
      return (
        <CreatedNotificationItem
          item={item as TaskTemplateCreatedNotification}
        />
      );
    case 'deleted':
      return (
        <DeletedNotificationItem
          item={item as TaskTemplateDeletedNotification}
        />
      );
    default:
      return <div>Unknown task template notification type: {actionType}</div>;
  }
}

function CreatedNotificationItem({
  item,
}: {
  item: TaskTemplateCreatedNotification;
}) {
  const { usersMap } = useUsersData();
  const { user } = useAuthStore();
  const actorName = getName(item.actor_id, usersMap);
  const viewerUserId = (DEBUG_MODE && item.debug_user_id) || user?.id;
  if (!viewerUserId) {
    return <div>User not found {DEBUG_MODE && viewerUserId}</div>;
  }

  const isPreparer = item.template.preparers.some(
    (preparer) => preparer.user_id === viewerUserId
  );
  const actorType = isPreparer ? 'preparer' : 'reviewer';

  return (
    <GenericNotificationItem
      item={item}
      url={closeChecklistURLs.base}
      avatar={<UserAvatar userId={item.actor_id} />}
      title={`${actorName} assigned you a recurring task as ${actorType}.`}
      description={<span>&quot;{item.template.core.name}&quot;</span>}
    />
  );
}

function DeletedNotificationItem({
  item,
}: {
  item: TaskTemplateDeletedNotification;
}) {
  const { usersMap } = useUsersData();
  const actorName = getName(item.actor_id, usersMap);

  return (
    <GenericNotificationItem
      item={item}
      url={closeChecklistURLs.base}
      avatar={<UserAvatar userId={item.actor_id} />}
      title={`${actorName} deleted a recurring task.`}
      description={<span>&quot;{item.template.core.name}&quot;</span>}
    />
  );
}
